import React, { useState } from 'react';

import { useObserver } from 'mobx-react-lite';
import { Button, Input, Notification } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Webhook } from '@bringg-frontend/webhooks-api-internal-stores';

import { isUrlValid } from '../../helpers';

interface Props {
	url: string;
	testable?: boolean;
	webhook: Webhook;
	uuid: string;
}

const WebhooksEditUrl: React.FC<Props> = ({ uuid, webhook, url, testable }) => {
	const { t } = useTranslation();
	const [webhookSent, setWebhookSent] = useState(false);

	const onTest = async () => {
		try {
			setWebhookSent(true);
			await webhook.test(uuid);
			Notification.success(t('WEBHOOK_APP.MODAL.TEST_BUTTON_SUCCESS', { defaultValue: 'Webhook sent!' }));
		} catch (error) {
			Notification.error(t('WEBHOOK_APP.MODAL.TEST_BUTTON_ERROR', { defaultValue: 'Webhook not sent!' }));
		} finally {
			setWebhookSent(false);
		}
	};

	return useObserver(() => {
		const isValidUrl = isUrlValid(url);
		const invalidUrlText = isValidUrl
			? undefined
			: t('WEBHOOK_APP.MODAL.INVALID_URL_ERROR', { defaultValue: 'Invalid URL' });

		return (
			<div className="webhooks-edit-url">
				<div className="url">
					<Input
						placeholder={t('WEBHOOK_APP.MODAL.URL_FIELD', { defaultValue: 'URL' })}
						onChange={e => webhook.updateUrl(uuid, e.target.value)}
						error={invalidUrlText}
						value={url}
					/>
				</div>
				<div className="delete">
					<Button
						ghost
						type="ghost"
						className="webhook-delete-button"
						shape="circle"
						onClick={() => webhook.deleteUrl(uuid)}
					>
						<BringgIcon iconName={BringgFontIcons.Trash} />
					</Button>
				</div>
				<div className="test">
					{testable ? (
						<Button
							className="gray-button button"
							disabled={!isValidUrl}
							loading={webhookSent}
							size="small"
							onClick={onTest}
						>
							{t('WEBHOOK_APP.MODAL.TEST_BUTTON', { defaultValue: 'Test' })}
						</Button>
					) : (
						''
					)}
				</div>
			</div>
		);
	});
};

export default WebhooksEditUrl;
